import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Drawer from "../common/Drawer";
import styles from "./HelpDrawer.module.css";
import {useViewModel} from "../../hooks/useViewModel.ts";

const headerNo = new URL("../../assets/callback_drawer_header_no.svg", import.meta.url).href;
const headerSe = new URL("../../assets/callback_drawer_header_se.svg", import.meta.url).href;
const headerDk = new URL("../../assets/callback_drawer_header_dk.svg", import.meta.url).href;

export const HELP_DRAWER_EVENT = "openhelpdrawerclick" as const;

const SUBMIT_STATE = {
	IDLE: "idle",
	SUCCESS: "success",
	ERROR: "error",
} as const;

type SubmitState = (typeof SUBMIT_STATE)[keyof typeof SUBMIT_STATE];

type StoreId = "310" | "324" | "325" | "312" | "318" | "344" | "321";

const HEADER_IMAGE: Record<StoreId, string> = {
	"310": headerNo,
	"324": headerNo,
	"325": headerNo,
	"312": headerSe,
	"318": headerSe,
	"344": headerSe,
	"321": headerDk,
};

const COUNTRY_CODE: Record<StoreId, string> = {
	"310": "+47",
	"324": "+47",
	"325": "+47",
	"312": "+46",
	"318": "+46",
	"344": "+46",
	"321": "+45",
};

const DEFAULT_STORE_ID: StoreId = "310";

const PHONE_REGEX = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{2,8}$/im;

type FormValues = {
	username: string;
	phoneNumber: string;
};

const HelpDrawer: React.FC = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [storeId] = useState<StoreId>(DEFAULT_STORE_ID);
	const [submitState, setSubmitState] = useState<SubmitState>(SUBMIT_STATE.IDLE);

	const { data } = useViewModel();

	const schema = useMemo(() =>
		yup.object({
			username: yup.string().required(data?.strings.product.configBundles.help.formError ?? "Required"),
			phoneNumber: yup.string()
				.required(data?.strings.product.configBundles.help.formError ?? "Required")
				.matches(PHONE_REGEX, data?.strings.product.configBundles.help.phoneError ?? ""),
		}), [data]);

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<FormValues>({
		resolver: yupResolver(schema),
	});

	useEffect(() => {
		const handleOpen = () => setIsOpen(true);
		document.addEventListener(HELP_DRAWER_EVENT, handleOpen);
		return () => {
			document.removeEventListener(HELP_DRAWER_EVENT, handleOpen);
		};
	}, []);

	const resetForm = useCallback(() => {
		reset();
		setSubmitState(SUBMIT_STATE.IDLE);
	}, [reset]);

	const handleClose = useCallback(() => {
		resetForm();
		setIsOpen(false);
	}, [resetForm]);

	const onSubmit = useCallback(async ({ username, phoneNumber }: FormValues) => {

		const fullPhone = `${COUNTRY_CODE[storeId]}${phoneNumber}`;

		try {
			const res = await fetch("/api-product/genesys/beCalledBack", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					callbackUserName: username,
					callbackNumbers: [fullPhone],
				}),
			});

			setSubmitState(res.ok ? SUBMIT_STATE.SUCCESS : SUBMIT_STATE.ERROR);
		} catch (err) {
			console.error("Help drawer submit failed:", err);
			setSubmitState(SUBMIT_STATE.ERROR);
		}
	}, [storeId]);

	return (
		<Drawer isOpen={isOpen} onClose={handleClose} ariaLabel="Få hjelp fra oss">
			<div className={styles.header}>
				<img
					src={HEADER_IMAGE[storeId]}
					alt=""
					className={styles.headerImage}
					aria-hidden="true"
				/>
			</div>

			<div className={styles.container}>
				<div className={styles.hero}>
					<h2 className={styles.heroTitle}>Få hjelp fra oss</h2>
					<p className={styles.heroDesc}>
						{data?.strings.product.configBundles.help.description}
					</p>
				</div>

				{submitState === SUBMIT_STATE.SUCCESS && (
					<div className={styles.form}>
						<p
							className={styles.successMessage}
							dangerouslySetInnerHTML={{ __html: data?.strings.product.configBundles.help.success ?? "" }}
						/>
					</div>
				)}

				{submitState === SUBMIT_STATE.ERROR && (
					<div className={styles.form}>
						<p className={styles.errorMessage}>
							{data?.strings.product.configBundles.help.error}
						</p>
					</div>
				)}

				{submitState === SUBMIT_STATE.IDLE && (
					<form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
						<input
							className={`${styles.input} ${errors.username ? styles.inputError : ""}`}
							placeholder={
								data?.strings.product.configBundles.help.namePlaceholder
							}
							data-testid="help-drawer-username-input"
							{...register("username")}
						/>

						<div
							className={`${styles.phoneWrapper} ${errors.phoneNumber ? styles.inputError : ""}`}
						>
							<span className={styles.countryCode}>
								{COUNTRY_CODE[storeId]}
							</span>
							<input
								className={styles.phoneInput}
								placeholder={
									data?.strings.product.configBundles.help.phonePlaceholder
								}
								autoComplete="tel"
								data-testid="help-drawer-phone-input"
								{...register("phoneNumber")}
							/>
						</div>

						{(errors.username?.message || errors.phoneNumber?.message) && (
							<p className={styles.errorMessage}>
								{errors.username?.message || errors.phoneNumber?.message}
							</p>
						)}

						<button
							type="submit"
							className={styles.submitButton}
							data-testid="help-drawer-submit-button"
						>
							{data?.strings.product.configBundles.help.submitButton}
						</button>
					</form>
				)}
			</div>
		</Drawer>
	);
};

export default HelpDrawer;